/**
 * Player.ts — the shark. Authoritative fixed-step XZ physics (heading-locked thrust, keel
 * damp, exact-distance lunge) plus its interpolated presentation (bank, pitch, bob,
 * swim-shader drive, pooled ghost trail). Owns one Object3D and nothing else about the scene.
 */

import * as THREE from 'three';
import { bus } from '../core/Events.ts';
import type { Input } from '../core/Input.ts';
import type { Controls } from './Autopilot.ts';
import { DEPTH, LUNGE, MOVEMENT, OCEAN, SWIM, WATER } from '../data/tuning.ts';
import { swimParamsForSpeed, type SharkMaterial } from '../render/SharkMaterial.ts';
import type { SharkMesh } from '../voxel/MeshService.ts';
import { TOKENS } from '../voxel/Palette.ts';
import { bedHeight, pushToWater, SPAWN } from '../world/Coast.ts';

const TAU = Math.PI * 2;
const DEG2RAD = Math.PI / 180;

/** CONTRACTS §5 states this bound for pitchFromAccel but `tuning` has no field for it. */
const PITCH_LIMIT = 0.25;
/** Extra nose-up/nose-down while climbing or diving, on top of the thrust pitch. */
const CLIMB_PITCH_LIMIT = 0.34;

/* --- the bite -------------------------------------------------------------------------
   Keyframed rather than physical, because a strike is a performance: anticipation, a held
   gape, a snap, and a rebound. `p` runs 0..1 over BITE_DUR. The gape opens through p=0.40,
   holds to 0.53, snaps shut by 0.60, then rebounds slightly — the hold is what makes the
   snap read as violent instead of as a wobble. */
const BITE_DUR = 0.62;
const BITE_SNAP = 0.60;
const JAW_MAX = 0.62;
const SNOUT_MAX = 0.17;
const SHAKE_MAX = 0.22;

type Key = readonly [number, number];
const K_JAW: Key[] = [[0, 0], [0.14, 0.12], [0.40, 1], [0.53, 1], [0.60, 0], [0.73, 0.07], [0.82, 0]];
const K_SNOUT: Key[] = [[0, 0], [0.14, 0.06], [0.40, 1], [0.53, 1], [0.60, 0], [1, 0]];
const K_SHAKE: Key[] = [[0.58, 0], [0.68, 1], [0.80, 0.75], [1, 0]];

function keyed(keys: Key[], p: number): number {
  if (p <= keys[0][0]) return keys[0][1];
  const n = keys.length;
  if (p >= keys[n - 1][0]) return keys[n - 1][1];
  for (let i = 0; i < n - 1; i++) {
    if (p <= keys[i + 1][0]) {
      const f = (p - keys[i][0]) / (keys[i + 1][0] - keys[i][0]);
      return lerp(keys[i][1], keys[i + 1][1], f * f * (3 - 2 * f));
    }
  }
  return keys[n - 1][1];
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Lunge velocity profile: v(t) = vExit + (vPeak - vExit) * (1 - t/D)^LUNGE_EASE.
 * Ending the curve *at* the exit speed (rather than at zero) means the dash flows straight
 * into the post-lunge glide with no discontinuity. EASE = 2 front-loads ~80% of the ground
 * into the first half — it reads as a stab, not a glide.
 */
const LUNGE_EASE = 2;
const LUNGE_EXIT_SPEED = MOVEMENT.cruiseSpeed * LUNGE.exitSpeedScale;
/**
 * Normalisation, derived not eyeballed: ∫₀ᴰ v dt = vExit·D + (vPeak − vExit)·D/(EASE+1),
 * set equal to LUNGE.distance and solved for vPeak. Any other peak and the dash covers
 * the wrong distance.
 */
const LUNGE_PEAK_SPEED =
  LUNGE_EXIT_SPEED +
  ((LUNGE.distance - LUNGE_EXIT_SPEED * LUNGE.duration) * (LUNGE_EASE + 1)) / LUNGE.duration;
const GHOST_INTERVAL = LUNGE.duration / LUNGE.ghostCount;

/**
 * Closed form of ∫₀ᵗ v dτ. Sampling *this* per step instead of the instantaneous velocity
 * makes the discrete integrator cover exactly LUNGE.distance; forward-Euler on a curve this
 * steep overshoots by ~2 u (10%).
 */
function lungeDistanceAt(t: number): number {
  const u = t <= 0 ? 0 : t >= LUNGE.duration ? 1 : t / LUNGE.duration;
  const p = LUNGE_EASE + 1;
  return (
    LUNGE_EXIT_SPEED * LUNGE.duration * u +
    ((LUNGE_PEAK_SPEED - LUNGE_EXIT_SPEED) * LUNGE.duration * (1 - Math.pow(1 - u, p))) / p
  );
}

/** shortest signed path from a to b, in (-π, π] */
function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return d;
}

function wrapAngle(a: number): number {
  let r = (a + Math.PI) % TAU;
  if (r < 0) r += TAU;
  return r - Math.PI;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

const GHOST_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vN = normalize(mat3(modelMatrix) * normal);
  vV = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

// Edge-weighted so the trail reads as an after-image outline instead of a solid glowing blob.
const GHOST_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
varying vec3 vN;
varying vec3 vV;
void main() {
  float rim = 1.0 - abs(dot(normalize(vN), normalize(vV)));
  rim *= rim;
  gl_FragColor = vec4(uColor * (0.10 + 0.90 * rim), uOpacity * (0.14 + 0.86 * rim));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const PROXY_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
#include <fog_pars_vertex>
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vN = normalize(mat3(modelMatrix) * normal);
  vV = normalize(cameraPosition - wp.xyz);
  vec4 mvPosition = viewMatrix * wp;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

// Same lighting vocabulary as the real shark material (half-lambert key + hemi ambient +
// fresnel rim) so the placeholder sits in the water correctly rather than looking pasted on.
const PROXY_FRAG = /* glsl */ `
uniform vec3 uToSun;
uniform vec3 uSunColor;
uniform vec3 uAmbientTop;
uniform vec3 uAmbientBottom;
uniform vec3 uRimColor;
uniform vec3 uDorsal;
uniform vec3 uVentral;
varying vec3 vN;
varying vec3 vV;
#include <fog_pars_fragment>
void main() {
  vec3 n = normalize(vN);
  vec3 v = normalize(vV);
  vec3 base = mix(uVentral, uDorsal, smoothstep(-0.30, 0.55, n.y));
  float key = dot(n, uToSun) * 0.5 + 0.5;
  vec3 amb = mix(uAmbientBottom, uAmbientTop, n.y * 0.5 + 0.5);
  float rim = pow(1.0 - max(dot(n, v), 0.0), 3.0);
  vec3 col = base * (amb + uSunColor * key * key) + uRimColor * rim * 0.35;
  gl_FragColor = vec4(col, 1.0);
  #include <fog_fragment>
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface PlayerVisual {
  /**
   * Add to the scene. Stays at the origin: the shark body is a child that carries the
   * interpolated transform, and the ghost trail hangs off the root so its frozen poses
   * stay in world space.
   */
  object: THREE.Object3D;
  material: SharkMaterial | null;
}

interface Ghost {
  mesh: THREE.Mesh;
  material: THREE.ShaderMaterial;
  life: number;
}

/**
 * The Y a shark settles to over a given patch of coast.
 *
 * Relative, not absolute. Over a lagoon 14 u deep and a reef flat 6 u deep one fixed height
 * is either scraping the coral or hugging the sand, so this measures a fraction of the water
 * column that is actually AVAILABLE — from the belly clearance up to the breach line.
 */
/** scratch for the shore push; the fixed step must not allocate */
const WATER_OUT = { x: 0, z: 0 };

export function cruiseY(x: number, z: number): number {
  const floor = bedHeight(x, z) + DEPTH.floorClearance;
  const room = OCEAN.surfaceHeight - DEPTH.breachSpan - floor;
  return floor + Math.max(0, room) * DEPTH.cruiseFraction;
}

export class Player {
  // --- simulation state (authoritative, stepped at fixed dt) ---
  x = SPAWN.x;
  z = SPAWN.z;
  /** depth: world Y of the body centre, from the sand up to the breach ceiling */
  y = cruiseY(SPAWN.x, SPAWN.z);
  vx = 0;
  vz = 0;
  vy = 0;
  heading = 0;

  readonly visual: PlayerVisual;

  private _prevX = 0;
  private _prevZ = 0;
  private _prevY = cruiseY(SPAWN.x, SPAWN.z);
  private _prevHeading = 0;

  private _lungeActive = false;
  private _lungeT = 0;
  private _cooldown = 0;
  private _iframes = 0;
  private _ampSpike = 0;
  /** seconds into the bite performance, or -1 when the jaws are shut */
  private _biteT = -1;

  /** signed fraction of the maximum turn used last step, -1..1 — drives the bank */
  private _turnSignal = 0;
  /** signed bend demand, -1..1 — drives the body curve; peaks at a standstill pivot */
  private _curveSignal = 0;
  /** unsigned sculling effort, 0..1 — drives the extra tail beat that powers a turn */
  private _turnEffort = 0;
  /** along-heading acceleration last step, u/s² — drives the pitch */
  private _fwdAccel = 0;
  private _prevForward = 0;

  private _renderX = 0;
  private _renderY = cruiseY(SPAWN.x, SPAWN.z);
  private _renderZ = 0;
  private _bank = 0;
  private _pitch = 0;
  private _swimAmp = SWIM.ampIdle;
  private _swimAmpMul = 1;
  /** eased lateral body bend, world units of tail offset */
  private _curve = 0;
  /** eased sculling effort, 0..1 */
  private _scull = 0;

  private readonly _root = new THREE.Group();
  private readonly _body = new THREE.Object3D();
  private readonly _proxy = new THREE.Group();
  private readonly _proxyMaterial: THREE.ShaderMaterial;
  private readonly _ghosts: Ghost[] = [];
  private _ghostNext = 0;
  private _ghostsQueued = 0;
  private _ghostTimer = 0;

  private _sharkMesh: THREE.Mesh | null = null;
  private _sharkGeometry: THREE.BufferGeometry | null = null;
  private readonly _ownedGeometry: THREE.BufferGeometry[] = [];

  constructor() {
    this._root.name = 'player';
    // Yaw → pitch → roll: roll must happen about the shark's own forward axis, which the
    // default XYZ order does not give you once the body is yawed.
    this._body.rotation.order = 'YXZ';
    this._body.position.y = cruiseY(SPAWN.x, SPAWN.z);
    this._root.add(this._body);

    this._proxyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        // This shader opts into three's fog chunks, and `fog: true` makes three write these
        // every frame via refreshFogUniforms — which dereferences them without checking.
        // Declaring the chunks without declaring the uniforms throws once per draw call.
        fogColor: { value: new THREE.Color() },
        fogDensity: { value: 0 },
        fogNear: { value: 1 },
        fogFar: { value: 1000 },
        // WATER.sunDir already points TOWARD the sun, which is what shading wants.
        uToSun: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: new THREE.Color().setHex(WATER.sunColor, THREE.SRGBColorSpace) },
        uAmbientTop: { value: new THREE.Color().setHex(WATER.ambientTop, THREE.SRGBColorSpace) },
        uAmbientBottom: {
          value: new THREE.Color().setHex(WATER.ambientBottom, THREE.SRGBColorSpace),
        },
        uRimColor: { value: new THREE.Color().setHex(WATER.rimColor, THREE.SRGBColorSpace) },
        uDorsal: { value: new THREE.Color().setHex(TOKENS.hydro, THREE.SRGBColorSpace) },
        uVentral: { value: new THREE.Color().setHex(TOKENS.bone, THREE.SRGBColorSpace) },
      },
      vertexShader: PROXY_VERT,
      fragmentShader: PROXY_FRAG,
      side: THREE.FrontSide,
      fog: true,
    });

    const hull = this.buildProxy();
    this._body.add(this._proxy);

    // Pooled at construction: a lunge must never allocate. Ghosts hang off the root so their
    // captured world transforms are not dragged along by the body.
    for (let i = 0; i < LUNGE.ghostCount; i++) {
      const material = this.makeGhostMaterial();
      const mesh = new THREE.Mesh(hull, material);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.renderOrder = 2;
      this._root.add(mesh);
      this._ghosts.push({ mesh, material, life: 0 });
    }

    this.visual = { object: this._root, material: null };
  }

  // ------------------------------------------------------------------ accessors

  get speed(): number {
    return Math.sqrt(this.vx * this.vx + this.vz * this.vz);
  }

  /** 0..1, speed / cruiseSpeed */
  get ventilation(): number {
    return Math.min(1, this.speed / MOVEMENT.cruiseSpeed);
  }

  get lungeActive(): boolean {
    return this._lungeActive;
  }

  /** seconds remaining, 0 when ready */
  get lungeCooldown(): number {
    return this._cooldown;
  }

  get invulnerable(): boolean {
    return this._iframes > 0;
  }

  get renderX(): number {
    return this._renderX;
  }

  get renderZ(): number {
    return this._renderZ;
  }

  get renderY(): number {
    return this._renderY;
  }

  /** gape in radians, for telemetry and for anything that wants to key off the strike */
  get gape(): number {
    return this._biteT < 0 ? 0 : keyed(K_JAW, this._biteT / BITE_DUR) * JAW_MAX;
  }

  /** true on the frames the jaws are closing — where a hit would land */
  get biting(): boolean {
    return this._biteT >= 0 && this._biteT / BITE_DUR >= BITE_SNAP;
  }

  /**
   * 0 fully submerged, 1 fully clear of the waterline. Drives the fin foam on the surface,
   * kills the swell bob, and is what the rest of the scene asks when it wants to know
   * whether the shark is showing.
   */
  get breach(): number {
    const t = (this.y - (OCEAN.surfaceHeight - DEPTH.breachSpan)) / DEPTH.breachSpan;
    return t < 0 ? 0 : t > 1 ? 1 : t;
  }

  // ------------------------------------------------------------------ simulation

  /**
   * Fixed-timestep physics. Reads Input, never touches THREE.
   *
   * `pilot`, when supplied, replaces the human's steer/throttle/dive/rise — and ONLY those.
   * Everything downstream is the same code path, which is the point: an autopiloted shark has
   * to have exactly the same momentum, turn-rate falloff, bank and body-bend as a driven one,
   * or the handover is visible. The lunge stays human-only; it is a decision, not a gait.
   */
  fixedUpdate(dt: number, input: Input, pilot: Controls | null = null): void {
    this._prevX = this.x;
    this._prevZ = this.z;
    this._prevY = this.y;
    this._prevHeading = this.heading;

    // --- depth ---------------------------------------------------------------
    // Vertical is its own axis, decoupled from the XZ physics: no keel, no quadratic drag,
    // just a commanded rate smoothed so the shark has mass. Combat and collision stay on XZ.
    const wantDive = input.isDown('dive');
    const wantRise = input.isDown('rise');
    let targetVy = 0;
    if (pilot) {
      // The autopilot commands a RATE, not a direction. See Controls.climb: a boolean pilot
      // needs a dead band to stop it hunting, and the band then eats the behaviour.
      targetVy = pilot.climb * (pilot.climb >= 0 ? DEPTH.riseSpeed : DEPTH.diveSpeed);
    } else if (wantDive && !wantRise) targetVy = -DEPTH.diveSpeed;
    else if (wantRise && !wantDive) targetVy = DEPTH.riseSpeed;
    else {
      /* Cruise depth is RELATIVE to the coast, not an absolute Y. Over a lagoon that is 14 u
         deep and a reef flat that is 6, one fixed height is either scraping the coral or
         hugging the sand — the shark has to settle into the water column it is actually in.
         Clamped so it never targets a depth shallower than its own belly clearance. */
      targetVy = (cruiseY(this.x, this.z) - this.y) * DEPTH.levelResponse;
    }

    this.vy += (targetVy - this.vy) * (1 - Math.exp(-DEPTH.response * dt));
    this.y += this.vy * dt;

    // The bed is terrain now, not a constant. floorClearance is how far the belly rides
    // ABOVE it, so a shark flying up the fore-slope is pushed up by the reef instead of
    // swimming through a spur.
    const bed = bedHeight(this.x, this.z) + DEPTH.floorClearance;
    if (this.y < bed) {
      this.y = bed;
      if (this.vy < 0) this.vy = 0;
    } else if (this.y > DEPTH.breachCeiling) {
      this.y = DEPTH.breachCeiling;
      if (this.vy > 0) this.vy = 0;
    }

    if (this._cooldown > 0) this._cooldown = Math.max(0, this._cooldown - dt);
    if (this._iframes > 0) this._iframes = Math.max(0, this._iframes - dt);
    if (this._ampSpike > 0) this._ampSpike = Math.max(0, this._ampSpike - dt);
    if (this._biteT >= 0) {
      this._biteT += dt;
      if (this._biteT >= BITE_DUR) this._biteT = -1;
    }

    // Consumed unconditionally: a press swallowed by the cooldown must not queue up and fire
    // itself seconds later.
    const wantLunge = input.consume('lunge');
    if (wantLunge && !this._lungeActive && this._cooldown <= 0) {
      this._lungeActive = true;
      this._lungeT = 0;
      this._cooldown = LUNGE.cooldown;
      this._iframes = LUNGE.iframes;
      this._ampSpike = LUNGE.ampSpikeTime;
      this._ghostsQueued = LUNGE.ghostCount;
      this._ghostTimer = 0;
      // The lunge IS the bite. Starting the jaw here rather than on contact means the gape
      // is already open when the dash arrives, which is what sells it as a strike.
      this._biteT = 0;
      bus.emit('player:lunge', { x: this.x, z: this.z, heading: this.heading });
    }

    // --- rudder. A/D are a RATE, not a target heading: hold one and the shark keeps turning,
    // all the way round and round. Steering at a target heading would stop the turn the
    // instant it was reached, which is the twin-stick behaviour SPEC §5.2 rules out.
    // The rudder still bites during a lunge, which is what makes the dash a carve.
    const sp = this.speed;
    const steer = clamp(pilot ? pilot.steer : input.steer, -1, 1);
    const throttle = clamp(pilot ? pilot.throttle : input.throttle, -1, 1);

    if (Math.abs(steer) > 1e-4) {
      const t = clamp(sp / MOVEMENT.cruiseSpeed, 0, 1);
      const turnRate =
        (MOVEMENT.turnRateRest + (MOVEMENT.turnRateFull - MOVEMENT.turnRateRest) * t) * DEG2RAD;
      // Screen-right is world -X for this camera rig (it sits astern on -Z with no yaw), and
      // heading grows from +Z toward +X. So a right rudder DECREASES the heading angle.
      const applied = -steer * turnRate * dt;
      this.heading = wrapAngle(this.heading + applied);

      // Three separate signals, because they are three different pieces of physics and
      // scaling them all the same way is what makes a standstill turn look like a plank.
      //
      // Roll needs lateral load, so it scales WITH speed — a shark pivoting on the spot has
      // nothing to lean against and stays flat.
      this._turnSignal = -steer * t;
      // Body bend is the opposite: strongest at rest, because a tight pivot is achieved by
      // folding the body, while a fast carve is a wide arc through a nearly straight body.
      this._curveSignal = -steer * (1 - (1 - SWIM.curveAtCruise) * t);
      // Sculling effort — the tail drives the turn when there is no forward speed to turn
      // against, so the stroke has to come up even though the animal is going nowhere.
      this._turnEffort = Math.abs(steer) * (1 - (1 - SWIM.turnEffortAtCruise) * t);
    } else {
      this._turnSignal = 0;
      this._curveSignal = 0;
      this._turnEffort = 0;
    }

    const hx = Math.sin(this.heading);
    const hz = Math.cos(this.heading);

    if (this._lungeActive) {
      // Velocity is fully overridden along the heading; drag and keel would only corrupt a
      // profile that is already exact. ds/dt makes `x += vx*dt` land on the analytic curve.
      const t0 = this._lungeT;
      const t1 = Math.min(t0 + dt, LUNGE.duration);
      let ds = lungeDistanceAt(t1) - lungeDistanceAt(t0);
      ds += LUNGE_EXIT_SPEED * (t0 + dt - t1); // any part of the step past the dash coasts
      const v = ds / dt;
      this.vx = hx * v;
      this.vz = hz * v;
      this._lungeT = t0 + dt;
    } else {
      // --- thrust, always along the CURRENT heading. The rudder decides where that points;
      // the throttle only decides how hard you drive it.
      if (Math.abs(throttle) > 1e-4) {
        const scale = throttle > 0 ? throttle : throttle * MOVEMENT.reverseThrustScale;
        let dv = MOVEMENT.acceleration * scale * dt;
        if (dv < 0) {
          // Reverse thrust is a brake, not a reverse gear — sharks do not swim backwards.
          const forward = this.vx * hx + this.vz * hz;
          dv = Math.max(dv, -Math.max(forward, 0));
        }
        this.vx += hx * dv;
        this.vz += hz * dv;
      }

      // --- drag
      const s = Math.sqrt(this.vx * this.vx + this.vz * this.vz);
      if (s > 1e-6) {
        let d = MOVEMENT.quadDrag * s * s + MOVEMENT.linearDrag * s;
        // Glide drag keys off the THROTTLE alone: holding a turn with no throttle is still
        // coasting, and should still bleed speed.
        if (Math.abs(throttle) <= 1e-4) d += MOVEMENT.glideDrag * s;
        const bleed = Math.min(d * dt, s);
        this.vx -= (this.vx / s) * bleed;
        this.vz -= (this.vz / s) * bleed;
      }

      // --- keel damp. Split velocity into along-heading and lateral, then bleed the lateral
      // part exponentially. This one line is the difference between a body in water and a
      // velocity vector: it is what makes turns carve and drifts recover.
      const along = this.vx * hx + this.vz * hz;
      const latX = this.vx - along * hx;
      const latZ = this.vz - along * hz;
      const keel = Math.exp(-MOVEMENT.lateralDamp * dt);
      this.vx = along * hx + latX * keel;
      this.vz = along * hz + latZ * keel;
    }

    this.x += this.vx * dt;
    this.z += this.vz * dt;

    /* The bay is the arena, and it is the ONLY bound. Every edge — the beach behind, the
       headlands at both ends, the drop-off in front — is the same thing: water too shallow to
       swim. So none of them is a coordinate limit; the shark is simply pushed back up the
       depth gradient, which handles a headland and a reef spur with the code that handles the
       tide line.

       Killing the inbound velocity as well as correcting the position matters: leaving it
       alive would have the shark grinding along the shore at full thrust. */
    const before = { x: this.x, z: this.z };
    pushToWater(this.x, this.z, WATER_OUT);
    if (WATER_OUT.x !== before.x || WATER_OUT.z !== before.z) {
      const nx = WATER_OUT.x - before.x;
      const nz = WATER_OUT.z - before.z;
      this.x = WATER_OUT.x;
      this.z = WATER_OUT.z;
      // Remove only the component driving INTO the shore; the along-shore component survives,
      // so the shark slides along a headland instead of stopping dead against it.
      const len = Math.hypot(nx, nz) || 1;
      const ux = nx / len, uz = nz / len;
      const into = this.vx * ux + this.vz * uz;
      if (into < 0) {
        this.vx -= ux * into;
        this.vz -= uz * into;
      }
    }

    // Exit after integrating so the final step's displacement stays exact.
    if (this._lungeActive && this._lungeT >= LUNGE.duration) {
      this._lungeActive = false;
      this.vx = hx * LUNGE_EXIT_SPEED;
      this.vz = hz * LUNGE_EXIT_SPEED;
    }

    const forward = this.vx * hx + this.vz * hz;
    this._fwdAccel = (forward - this._prevForward) / dt;
    this._prevForward = forward;
  }

  // ------------------------------------------------------------------ presentation

  /** interpolated presentation. alpha in [0,1) between the last two fixed states. */
  render(alpha: number, dt: number, time: number): void {
    // Ghosts first, so a lunge's opening sample captures the launch pose rather than a
    // position four metres downrange.
    this.updateGhosts(dt);

    this._renderX = this._prevX + (this.x - this._prevX) * alpha;
    this._renderZ = this._prevZ + (this.z - this._prevZ) * alpha;
    this._renderY = this._prevY + (this.y - this._prevY) * alpha;
    const heading = this._prevHeading + angleDelta(this._prevHeading, this.heading) * alpha;

    const vent = this.ventilation;
    // The bob is swell riding the body; it has to die out near the surface or the shark
    // visibly pumps through the waterline while breaching.
    const submerged = 1 - this.breach;
    const bob =
      Math.sin(time * MOVEMENT.bobFrequency * TAU) *
      MOVEMENT.bobAmplitude *
      (0.35 + 0.65 * vent) *
      submerged;

    this._body.position.set(this._renderX, this._renderY + bob, this._renderZ);
    this._body.rotation.y = heading;

    // Attitude channels share bankResponse: they are the same physical idea (the body lagging
    // behind the change in motion) and a second rate would only desynchronise them.
    const attitude = 1 - Math.exp(-MOVEMENT.bankResponse * dt);
    this._bank += (-this._turnSignal * MOVEMENT.bankMax - this._bank) * attitude;
    this._body.rotation.z = this._bank;

    // Nose lifts under thrust, dips when the water takes the speed back — a bow, not a plane.
    // Climb pitch is added on top and dominates: a shark going up points up, and without it
    // a dive reads as the whole model being slid down the screen on rails.
    const targetPitch =
      -clamp(MOVEMENT.pitchFromAccel * this._fwdAccel, -PITCH_LIMIT, PITCH_LIMIT) -
      clamp(DEPTH.pitchPerSpeed * this.vy, -CLIMB_PITCH_LIMIT, CLIMB_PITCH_LIMIT);
    this._pitch += (targetPitch - this._pitch) * attitude;
    this._body.rotation.x = this._pitch;

    const mat = this.visual.material;
    if (mat) {
      // Cap what the shader sees: the lunge peaks near 250 u/s and would otherwise blow the
      // undulation amplitude out to twenty times the body width.
      const swim = swimParamsForSpeed(
        Math.min(this.speed, LUNGE_EXIT_SPEED),
        MOVEMENT.cruiseSpeed,
      );
      const ease = 1 - Math.exp(-SWIM.ampResponse * dt);
      this._swimAmp += (swim.amp - this._swimAmp) * ease;
      // Spike is pinned, not lerped — ampSpikeTime (0.12 s) is shorter than the response
      // time constant, so easing *into* it would never reach it.
      if (this._ampSpike > 0) this._swimAmpMul = LUNGE.ampSpike;
      else this._swimAmpMul += (1 - this._swimAmpMul) * ease;

      // Sculling rides on top of the speed-derived stroke, so a shark turning on the spot
      // still works its tail. Eased on the same constant as the amplitude so it arrives
      // with the rest of the stroke rather than snapping in.
      this._scull += (this._turnEffort - this._scull) * ease;
      const amp = this._swimAmp * this._swimAmpMul + SWIM.ampTurn * this._scull;
      mat.setSwim(amp, swim.speed + SWIM.speedTurn * this._scull);
      mat.setTime(time);

      // Body bend, eased separately from the roll so the arc trails the lean.
      this._curve += (this._curveSignal * SWIM.curveMax - this._curve) *
        (1 - Math.exp(-SWIM.curveResponse * dt));
      mat.setCurve(this._curve);

      // Bite. Sign matters and is easy to get backwards: the mandible sits FORWARD of its
      // hinge (+Z is the nose here), so a POSITIVE jaw angle drops the tip and opens the
      // gape, while the rostrum is the mirror case and takes a negative angle to lift.
      if (this._biteT >= 0) {
        const p = this._biteT / BITE_DUR;
        const shake =
          keyed(K_SHAKE, p) * SHAKE_MAX * Math.sin((p - 0.58) * Math.PI * 2 * 3.4);
        mat.setBite(keyed(K_JAW, p) * JAW_MAX, -keyed(K_SNOUT, p) * SNOUT_MAX, shake);
      } else {
        mat.setBite(0, 0, 0);
      }
    }
  }

  private updateGhosts(dt: number): void {
    for (let i = 0; i < this._ghosts.length; i++) {
      const g = this._ghosts[i];
      if (g.life <= 0) continue;
      g.life -= dt;
      if (g.life <= 0) {
        g.life = 0;
        g.mesh.visible = false;
        g.material.uniforms.uOpacity.value = 0;
        continue;
      }
      const f = g.life / LUNGE.ghostLife;
      g.material.uniforms.uOpacity.value = f * f;
    }

    if (this._ghostsQueued > 0) {
      this._ghostTimer -= dt;
      if (this._ghostTimer <= 0) {
        const g = this._ghosts[this._ghostNext];
        this._ghostNext = (this._ghostNext + 1) % this._ghosts.length;
        g.mesh.position.copy(this._body.position);
        g.mesh.quaternion.copy(this._body.quaternion);
        g.mesh.scale.copy(this._body.scale);
        g.life = LUNGE.ghostLife;
        g.material.uniforms.uOpacity.value = 1;
        g.mesh.visible = true;
        this._ghostsQueued--;
        this._ghostTimer += GHOST_INTERVAL;
      }
    }
  }

  // ------------------------------------------------------------------ lifecycle

  /** attach a built shark; disposes any previous geometry */
  setMesh(mesh: SharkMesh, material: SharkMaterial): void {
    if (this._sharkGeometry && this._sharkGeometry !== mesh.geometry) {
      this._sharkGeometry.dispose();
    }
    this._sharkGeometry = mesh.geometry;

    if (!this._sharkMesh) {
      this._sharkMesh = new THREE.Mesh(mesh.geometry, material);
      this._body.add(this._sharkMesh);
    } else {
      this._sharkMesh.geometry = mesh.geometry;
      this._sharkMesh.material = material;
    }
    this._sharkMesh.visible = true;
    this.visual.material = material;
    // Hand the model's articulation to the shader. Null parks the jaw stages, which is what
    // every species except the hand-tuned tiger gets.
    material.setJawRig(mesh.jaw);
    material.setSpineLength(mesh.size.z);

    // The trail is the real silhouette from here on.
    for (let i = 0; i < this._ghosts.length; i++) this._ghosts[i].mesh.geometry = mesh.geometry;

    this._proxy.visible = false;
  }

  /** @param heading which way it faces afterwards; the teaser reel cuts between locations */
  reset(x = 0, z = 0, heading = 0): void {
    this.x = this._prevX = this._renderX = x;
    this.z = this._prevZ = this._renderZ = z;
    this.vx = this.vz = 0;
    this.heading = this._prevHeading = heading;
    this._lungeActive = false;
    this._lungeT = 0;
    this._cooldown = 0;
    this._iframes = 0;
    this._ampSpike = 0;
    this._turnSignal = 0;
    this._fwdAccel = 0;
    this._prevForward = 0;
    this._bank = 0;
    this._pitch = 0;
    this._swimAmp = SWIM.ampIdle;
    this._swimAmpMul = 1;
    this._ghostsQueued = 0;
    this._ghostTimer = 0;
    for (let i = 0; i < this._ghosts.length; i++) {
      const g = this._ghosts[i];
      g.life = 0;
      g.mesh.visible = false;
      g.material.uniforms.uOpacity.value = 0;
    }
    this.y = this._prevY = this._renderY = cruiseY(x, z);
    this.vy = 0;
    this._body.position.set(x, this.y, z);
    this._body.rotation.set(0, heading, 0);
  }

  dispose(): void {
    for (let i = 0; i < this._ghosts.length; i++) {
      const g = this._ghosts[i];
      this._root.remove(g.mesh);
      g.material.dispose();
    }
    this._ghosts.length = 0;
    if (this._sharkMesh) {
      this._body.remove(this._sharkMesh);
      this._sharkMesh = null;
    }
    if (this._sharkGeometry) {
      this._sharkGeometry.dispose();
      this._sharkGeometry = null;
    }
    for (let i = 0; i < this._ownedGeometry.length; i++) this._ownedGeometry[i].dispose();
    this._ownedGeometry.length = 0;
    this._proxyMaterial.dispose();
    this._proxy.clear();
    this._body.clear();
    this._root.clear();
    this.visual.material = null;
  }

  // ------------------------------------------------------------------ internals

  private makeGhostMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color().setHex(WATER.rimColor, THREE.SRGBColorSpace) },
        uOpacity: { value: 0 },
      },
      vertexShader: GHOST_VERT,
      fragmentShader: GHOST_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    });
  }

  /**
   * Phase 0 stand-in: the "grey box" the movement is judged against, but shaped enough that
   * heading, bank and turn radius are readable. Hidden the instant setMesh() lands. The
   * proportions below are world units matched to a ~6 u voxel shark; they are model data,
   * not gameplay tunables. Returns the hull geometry, which the ghost pool borrows until a
   * real mesh exists.
   */
  private buildProxy(): THREE.BufferGeometry {
    const R = 0.62; // body half-width
    const L = 3.9; // capsule mid-section, total length ≈ L + 2R, squashed below
    const SWEEP = 0.55; // pectoral rake, radians

    const hull = new THREE.CapsuleGeometry(R, L, 4, 14);
    hull.rotateX(Math.PI / 2); // capsules are Y-axis; the shark is +Z forward
    hull.scale(1.06, 1.24, 1.0); // laterally compressed, taller than wide
    this._ownedGeometry.push(hull);

    const dorsal = new THREE.BoxGeometry(0.14, 1.15, 0.95);
    const caudal = new THREE.BoxGeometry(0.13, 1.95, 1.15);
    const pec = new THREE.BoxGeometry(1.25, 0.1, 0.66).translate(0.62, 0, 0);
    this._ownedGeometry.push(dorsal, caudal, pec);

    const hullMesh = new THREE.Mesh(hull, this._proxyMaterial);

    const dorsalMesh = new THREE.Mesh(dorsal, this._proxyMaterial);
    dorsalMesh.position.set(0, 0.92, -0.2);
    dorsalMesh.rotation.x = -0.42; // rakes back

    const caudalMesh = new THREE.Mesh(caudal, this._proxyMaterial);
    caudalMesh.position.set(0, 0.42, -2.7);
    caudalMesh.rotation.x = -0.5;

    const pecL = new THREE.Mesh(pec, this._proxyMaterial);
    pecL.position.set(0.42, -0.26, 0.6);
    pecL.rotation.y = SWEEP;

    // Mirrored by rotation rather than a negative scale, which would invert the winding and
    // break FrontSide culling.
    const pecR = new THREE.Mesh(pec, this._proxyMaterial);
    pecR.position.set(-0.42, -0.26, 0.6);
    pecR.rotation.y = Math.PI - SWEEP;

    this._proxy.add(hullMesh, dorsalMesh, caudalMesh, pecL, pecR);
    return hull;
  }
}
